import { useMemo } from "react";
import { useProjectAttachmentsList } from "./useProjectAttachmentsList";
import type { ProjectAttachment } from "../types";
import type { AttachmentTypeHolders } from "./attachmentSaveError";

const typeIdsOf = (rows: ProjectAttachment[], excludeId?: string) =>
  new Set(
    rows
      .filter((r) => r.id !== excludeId)
      .map((r) => r.attachmentTypeId)
      .filter(Boolean) as string[]
  );

// Attachment types already held on this project, split by active/archived.
// The API's uniqueness check counts both, so both matter. `excludeId` drops the
// attachment being edited, which may keep its own type.
export const useAttachmentTypeHolders = (
  projectId: string,
  excludeId?: string
): AttachmentTypeHolders => {
  const { rows: activeRows } = useProjectAttachmentsList({
    projectId,
    active: true,
  });
  const { rows: archivedRows } = useProjectAttachmentsList({
    projectId,
    active: false,
  });

  const activeTypeIds = useMemo(
    () => typeIdsOf(activeRows, excludeId),
    [activeRows, excludeId]
  );
  const archivedTypeIds = useMemo(
    () => typeIdsOf(archivedRows, excludeId),
    [archivedRows, excludeId]
  );

  return { activeTypeIds, archivedTypeIds };
};

export default useAttachmentTypeHolders;
